import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Heart, ShoppingBag } from "lucide-react";
import api from "@/lib/api";
import { useWishlist, useCart } from "@/contexts/ShopContext";
import { useCurrency } from "@/contexts/CurrencyContext";
import Navbar from "@/components/Navbar";
import FooterSection from "@/components/FooterSection";
import { toast } from "sonner";

export default function Wishlist() {
  const { ids, toggle, count } = useWishlist();
  const { formatAmount } = useCurrency();
  const { addItem } = useCart();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (ids.length === 0) { setProducts([]); setLoading(false); return; }
    Promise.all(ids.map((id) => api.get(`/products/${id}`).then((r) => r.data).catch(() => null)))
      .then((arr) => setProducts(arr.filter(Boolean)))
      .finally(() => setLoading(false));
  }, [ids]);

  const { currency } = useCurrency();

  return (
    <div className="bg-midnight min-h-screen">
      <Navbar />
      <main className="pt-28 md:pt-36 pb-20 px-6 md:px-12 lg:px-24" data-testid="wishlist-page">
        <div className="mb-12">
          <div className="eyebrow mb-3">Your Account</div>
          <h1 className="font-serif text-white text-4xl md:text-6xl leading-[0.95]">
            The <span className="italic text-silver">wishlist</span>.
          </h1>
          <p className="text-white/50 mt-3">{count} piece{count === 1 ? "" : "s"} saved.</p>
        </div>

        {loading ? (
          <div className="text-white/50">Loading…</div>
        ) : products.length === 0 ? (
          <div className="border border-white/10 p-12 text-center">
            <Heart size={32} strokeWidth={1.1} className="text-silver/70 mx-auto mb-4" />
            <h3 className="font-serif text-white text-3xl">Nothing saved yet.</h3>
            <p className="text-white/55 mt-3">Tap the heart on a piece to add it here.</p>
            <Link
              to="/"
              className="mt-8 inline-flex bg-white text-black px-7 py-3 text-[11px] tracking-[0.32em] uppercase hover:bg-silver"
            >Explore the house</Link>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5 md:gap-8">
            {products.map((p) => (
              <div key={p.id} className="group" data-testid={`wishlist-card-${p.id}`}>
                <div className="aspect-[3/4] overflow-hidden bg-midnight-panel mb-4 relative">
                  <Link to={`/product/${p.id}`}>
                    <img src={p.image} alt={p.name} className="w-full h-full object-cover product-img" />
                  </Link>
                  <button
                    aria-label="Remove from wishlist"
                    onClick={() => { toggle(p.id); toast.success("Removed."); }}
                    className="absolute top-3 right-3 w-9 h-9 bg-midnight-deep/70 backdrop-blur border border-white/15 flex items-center justify-center text-silver hover:text-white"
                    data-testid={`wishlist-remove-${p.id}`}
                  >
                    <Heart size={14} fill="currentColor" />
                  </button>
                </div>
                <div className="text-[10px] tracking-[0.3em] uppercase text-silver/70">{p.category}</div>
                <Link to={`/product/${p.id}`} className="font-serif text-white text-lg md:text-xl leading-tight block mt-1 hover:text-silver">
                  {p.name}
                </Link>
                <div className="flex items-center justify-between mt-2">
                  <span className="text-silver text-sm">
                    {formatAmount(currency === "INR" ? p.price_inr : p.price_usd)}
                  </span>
                  <button
                    onClick={() => {
                      addItem({
                        product_id: p.id,
                        size: (p.sizes?.find((s) => s.in_stock) || {}).label,
                        color: (p.colors?.find((c) => c.in_stock) || {}).name,
                        quantity: 1,
                      });
                      toast.success("Added to bag.");
                    }}
                    aria-label="Add to bag"
                    data-testid={`wishlist-tobag-${p.id}`}
                    className="text-white/60 hover:text-white"
                  >
                    <ShoppingBag size={16} strokeWidth={1.2} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
      <FooterSection />
    </div>
  );
}
